import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../lib/apiClient';
import { toast } from '../lib/toast';
import i18n from '../lib/i18n';
import type { Video } from '../types/video';
import { AIRECORDING_VIDEOS_REFRESH_EVENT, broadcastVideosRefresh } from './useVideos';

/** Same period as the server (BIN_RETENTION_DAYS): binned videos are purged after it. */
export const BIN_RETENTION_DAYS = 30;

/** `binPausedAt`: its 30 days are paused (Legal Hold, or the school not Active in Archive Mode). */
export type BinnedVideo = Video & { deletedAt: string; purgeAt: string; binPausedAt?: string };

/**
 * The user's Recycle Bin; reloads whenever videos change anywhere in the app, and when `owner`
 * (the signed-in user) changes.
 */
export const useRecycleBin = (enabled = true, owner?: string | null) => {
  const [videos, setVideos] = useState<BinnedVideo[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Only the latest load is applied (an older answer arriving last must not win).
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    const { data, error } = await apiClient.listRecycleBin();
    if (mine !== seq.current) return;
    if (error) setFailed(true);
    else { setFailed(false); setVideos((data ?? []) as BinnedVideo[]); }
  }, []);

  useEffect(() => {
    setVideos(null);
    if (!enabled) { seq.current++; return; } // and drop an answer still on its way
    void load();
    const onRefresh = () => void load();
    window.addEventListener(AIRECORDING_VIDEOS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(AIRECORDING_VIDEOS_REFRESH_EVENT, onRefresh);
  }, [enabled, owner, load]);

  return { videos, failed, reload: load };
};

/** Runs `action` on each video, one at a time; returns the ids that failed. Refreshes all lists once. */
export const forEachVideo = async (ids: string[], action: (id: string) => Promise<{ error: unknown }>) => {
  const failed: string[] = [];
  for (const id of ids) if ((await action(id)).error) failed.push(id);
  broadcastVideosRefresh();
  return failed;
};

export const restoreVideos = (ids: string[]) => forEachVideo(ids, (id) => apiClient.restoreVideo(id));

/** "Moved to the Recycle bin", with Undo (restores them). */
export const toastMovedToBin = (ids: string[]) => {
  if (!ids.length) return;
  toast.success(i18n.t('recycle_bin.moved', { count: ids.length }), {
    duration: 8000,
    action: {
      label: i18n.t('recycle_bin.undo', 'Undo'),
      onClick: async () => {
        const failed = await restoreVideos(ids);
        if (failed.length) toast.error(i18n.t('recycle_bin.restore_failed', 'Some videos could not be restored. Try again from the Recycle bin.'));
        else toast.success(i18n.t('recycle_bin.restored', { count: ids.length }));
      },
    },
  });
};
